import React, { useState, useEffect } from 'react';
import { Card, DonutChart, Flex, Metric, Text, Button, LineChart } from '@tremor/react';
import { PlayIcon, PauseIcon, ChartBarIcon, SpeakerWaveIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

// Custom Tooltip Component
function CustomTooltip({ children, tooltip }) {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div 
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && tooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50 w-80 max-w-sm break-words">
                    {tooltip}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
}

function AudioEmotionAnalysisCard({ fragment, analysisParentUuid, onStartAnalysis, onCancelAnalysis, ...rest }) {
    const { t } = useTranslation();
    const [selectedView, setSelectedView] = useState('emotions'); // 'emotions' or 'timeline'
    const [isTimedOut, setIsTimedOut] = useState(false);
    const [timeInterval, setTimeInterval] = useState(3); // Time interval in seconds for timeline granularity
    
    // Timeout logic for hanging fragments
    useEffect(() => {
        if (!fragment) return;
        
        const pendingStatuses = ['processing', 'pending_webhook', 'queued'];
        if (pendingStatuses.includes(fragment.status)) {
            // Set timeout for 5 minutes (300000ms) for pending analysis
            const timeoutId = setTimeout(() => {
                setIsTimedOut(true);
            }, 300000); // 5 minutes
            
            return () => clearTimeout(timeoutId);
        } else {
            // Reset timeout if status changes to completed/failed
            setIsTimedOut(false);
        }
    }, [fragment?.status, fragment?.uuid]);
    
    // Handle missing or loading fragment - don't show card by default
    if (!fragment) {
        return null;
    }

    // Handle timeout for pending analysis
    if ((fragment.status === 'processing' || fragment.status === 'pending_webhook' || fragment.status === 'queued') && isTimedOut) {
        return (
            <Card className="max-h-full" data-testid="emotion-analysis-card" {...rest}>
                <div className="p-4">
                    <Text className="font-medium text-gray-900">
                        {t('EmotionAnalysis.Title', 'Audio Emotion Analysis')}
                    </Text>
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <Text className="text-red-700 font-medium">
                            {t('EmotionAnalysis.Timeout', 'Analysis Timeout')}
                        </Text>
                        <Text className="mt-1 text-sm text-red-600">
                            {t('EmotionAnalysis.TimeoutDescription', 'It was not possible to complete the audio emotion analysis. The analysis took too long to process.')}
                        </Text>
                    </div>
                </div>
            </Card>
        );
    }

    // Handle different fragment statuses (still pending, not timed out yet)
    if (fragment.status === 'processing' || fragment.status === 'pending_webhook' || fragment.status === 'queued') {
        return (
            <Card className="max-h-full" data-testid="emotion-analysis-card" {...rest}>
                <div className="p-4">
                    <Text className="font-medium text-gray-900">
                        {t('EmotionAnalysis.Title', 'Audio Emotion Analysis')}
                    </Text>
                    <div className="mt-4 flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-iavisus-100"></div>
                        <Text>{getStatusMessage(fragment.status, t)}</Text>
                    </div>
                    {fragment.notes && (
                        <Text className="mt-2 text-sm text-gray-500">{fragment.notes}</Text>
                    )}
                </div>
            </Card>
        );
    }

    // Handle failed analysis - don't show card when analysis fails
    if (fragment.status === 'failed') {
        return null;
    }

    // Handle service unavailable - don't show card when service is unavailable
    if (fragment.status === 'service_unavailable') {
        return null;
    }

    // Handle completed analysis
    if (fragment.status === 'completed' && fragment.result) {
        try {
            const results = typeof fragment.result === 'string' ? JSON.parse(fragment.result) : fragment.result;
            const overallResults = results.overall_results;
            const timeSeriesResults = overallResults?.time_series || results.time_series_results || [];
            
            if (!overallResults) {
                throw new Error('No overall results found');
            }

            // Process segments to calculate emotion distribution
            const emotionDistribution = calculateEmotionDistributionFromSegments(overallResults.segments);
            
            // If no emotion distribution could be calculated, don't show the card
            if (!emotionDistribution || Object.keys(emotionDistribution).length === 0) {
                return null;
            }
            
            const dominantEmotion = getDominantEmotionFromDistribution(emotionDistribution);
            const averageConfidence = calculateAverageConfidence(overallResults.segments);
            const duration = overallResults.audio_info?.duration || overallResults.processing_stats?.total_duration || 0;
            const segmentCount = overallResults.segments?.length || 0;


            return (
                <Card className="max-h-full" data-testid="emotion-analysis-card" {...rest}>
                    <div className="p-4">
                        <Flex alignItems="center" justifyContent="between" className="mb-4">
                            <div className="flex items-center space-x-2">
                                <Text className="font-medium text-gray-900">
                                    {t('EmotionAnalysis.Title', 'Audio Emotion Analysis')}
                                </Text>
                                <CustomTooltip 
                                    tooltip={
                                        <div className="text-left max-w-xs break-words">
                                            <div className="font-semibold text-white mb-2">
                                                {t('EmotionAnalysis.HowItWorks.Title', 'How Audio Emotion Analysis Works')}
                                            </div>
                                            <div className="text-gray-200 text-xs leading-relaxed">
                                                {t('EmotionAnalysis.HowItWorks.Description', 
                                                    'This analysis uses advanced speech emotion recognition based on wav2vec2-XLSR and SenseVoice AI models. Our system analyzes voice patterns, tone, and prosodic features to identify emotions in speech across any language. Percentages show confidence levels for each detected emotion - they sum to 100% because each audio segment represents one emotional state. The analysis is based on Ekman\'s universal emotion research and cross-lingual speech processing.'
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                </CustomTooltip>
                            </div>
                            <div className="flex space-x-2">
                                <Button
                                    icon={SpeakerWaveIcon}
                                    onClick={() => setSelectedView('emotions')}
                                    className={`${selectedView === 'emotions' ? '!bg-iavisus-100 !text-white !border-iavisus-100' : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'}`}
                                    size="sm"
                                >
                                    {t('EmotionAnalysis.Emotions', 'Emotions')}
                                </Button>
                                {timeSeriesResults.length > 0 && (
                                    <Button
                                        icon={ChartBarIcon}
                                        onClick={() => setSelectedView('timeline')}
                                        className={`${selectedView === 'timeline' ? '!bg-iavisus-100 !text-white !border-iavisus-100' : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'}`}
                                        size="sm"
                                    >
                                        {t('EmotionAnalysis.Timeline', 'Timeline')}
                                    </Button>
                                )}
                            </div>
                        </Flex>


                        {selectedView === 'emotions' && emotionDistribution && (
                            <div>
                                <Text className="mb-4">{t('EmotionAnalysis.EmotionDistribution', 'Emotion Distribution')}</Text>
                                
                                <Flex className="space-x-3 truncate mb-4" justifyContent="start" alignItems="baseline">
                                    <Metric>{translateEmotionName(dominantEmotion || 'neutral', t)}</Metric>
                                </Flex>

                                <DonutChart
                                    className="mt-4 h-64"
                                    data={Object.entries(emotionDistribution || {}).map(([emotion, value]) => ({
                                        name: translateEmotionName(emotion, t),
                                        value: Math.round((value || 0) * 100)
                                    })).filter(item => item.value > 0)}
                                    category="value"
                                    index="name"
                                    colors={["emerald", "blue", "red", "violet", "orange", "lime", "gray"]}
                                    showAnimation={true}
                                    variant="pie"
                                    valueFormatter={(value) => `${value || 0}%`}
                                    showLegend={true}
                                    showTooltip={true}
                                />

                                <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                    {Object.entries(emotionDistribution || {})
                                        .sort(([,a], [,b]) => (b || 0) - (a || 0))
                                        .filter(([, value]) => (value || 0) > 0)
                                        .map(([emotion, value]) => (
                                        <div key={emotion} className="flex items-center space-x-2">
                                            <div 
                                                className="w-3 h-3 rounded-full" 
                                                style={{
                                                    backgroundColor: getEmotionColor(emotion)
                                                }}
                                            ></div>
                                            <Text className="text-sm text-gray-700">
                                                {translateEmotionName(emotion, t)} ({Math.round((value || 0) * 100)}%)
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedView === 'timeline' && timeSeriesResults && timeSeriesResults.length > 0 && (
                            <div>
                                <div className="mb-4 flex items-center justify-between">
                                    <Text className="font-medium text-gray-900">{t('EmotionAnalysis.EmotionTimeline', 'Emotion Timeline')}</Text>
                                    
                                    {/* Granularity Selector */}
                                    <div className="flex space-x-1">
                                        <Text className="text-xs text-gray-500 mr-2 self-center">
                                            {t('EmotionAnalysis.Granularity', 'Granularity')}:
                                        </Text>
                                        {[1, 3, 5, 10].map((interval) => (
                                            <Button
                                                key={interval}
                                                onClick={() => setTimeInterval(interval)}
                                                className={`${timeInterval === interval 
                                                    ? '!bg-iavisus-100 !text-white !border-iavisus-100' 
                                                    : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'
                                                } !text-xs !px-2 !py-1`}
                                                size="sm"
                                            >
                                                {interval}s
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                                
                                <LineChart
                                    className="h-64"
                                    data={(() => {
                                        // Group audio data into user-selected intervals and calculate averages
                                        const intervalSize = timeInterval;
                                        const intervals = new Map();
                                        
                                        timeSeriesResults.forEach((point, index) => {
                                            const timeInSeconds = point.second || point.start_time || point.timestamp || index;
                                            const intervalKey = Math.floor(timeInSeconds / intervalSize) * intervalSize;
                                            
                                            if (!intervals.has(intervalKey)) {
                                                intervals.set(intervalKey, {
                                                    timestamp: formatTimestamp(intervalKey),
                                                    points: []
                                                });
                                            }
                                            
                                            intervals.get(intervalKey).points.push(point);
                                        });
                                        
                                        // Define all possible emotions to ensure consistent tooltip display
                                        const allEmotions = ['happiness', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];
                                        
                                        // Calculate averages for each interval
                                        return Array.from(intervals.values()).map(interval => {
                                            const dataPoint = { timestamp: interval.timestamp };
                                            const emotionSums = {};
                                            const emotionCounts = {};
                                            
                                            // Initialize all emotions to ensure they appear in tooltips
                                            allEmotions.forEach(emotion => {
                                                const translatedName = translateEmotionName(emotion, t);
                                                emotionSums[translatedName] = 0;
                                                emotionCounts[translatedName] = 0;
                                            });
                                            
                                            interval.points.forEach(point => {
                                                try {
                                                    if (point.emotions && typeof point.emotions === 'object') {
                                                        Object.entries(point.emotions).forEach(([emotion, value]) => {
                                                            if (emotion && value !== undefined && value !== null) {
                                                                const translatedName = translateEmotionName(emotion, t);
                                                                emotionSums[translatedName] = (emotionSums[translatedName] || 0) + (value || 0);
                                                                emotionCounts[translatedName] = (emotionCounts[translatedName] || 0) + 1;
                                                            }
                                                        });
                                                    } else if (point.emotion_scores && typeof point.emotion_scores === 'object') {
                                                        Object.entries(point.emotion_scores).forEach(([emotion, value]) => {
                                                            if (emotion && value !== undefined && value !== null) {
                                                                const translatedName = translateEmotionName(emotion, t);
                                                                emotionSums[translatedName] = (emotionSums[translatedName] || 0) + (value || 0);
                                                                emotionCounts[translatedName] = (emotionCounts[translatedName] || 0) + 1;
                                                            }
                                                        });
                                                    } else {
                                                        emotionSums.Confidence = (emotionSums.Confidence || 0) + (point.confidence || 0);
                                                        emotionCounts.Confidence = (emotionCounts.Confidence || 0) + 1;
                                                    }
                                                } catch (e) {
                                                    emotionSums.Confidence = (emotionSums.Confidence || 0) + (point.confidence || 0);
                                                    emotionCounts.Confidence = (emotionCounts.Confidence || 0) + 1;
                                                }
                                            });
                                            
                                            // Calculate averages and convert to percentages
                                            Object.keys(emotionSums).forEach(emotion => {
                                                if (emotionCounts[emotion] > 0) {
                                                    dataPoint[emotion] = Math.round((emotionSums[emotion] / emotionCounts[emotion]) * 100);
                                                } else {
                                                    // Always include all emotions in tooltips, even if 0%
                                                    dataPoint[emotion] = 0;
                                                }
                                            });
                                            
                                            return dataPoint;
                                        }).sort((a, b) => {
                                            // Sort by timestamp for proper chart ordering
                                            // Convert "M:SS" format back to seconds for accurate sorting
                                            const parseTimestamp = (timestamp) => {
                                                if (!timestamp) return 0;
                                                const [minutes, seconds] = timestamp.split(':').map(Number);
                                                return (minutes || 0) * 60 + (seconds || 0);
                                            };
                                            
                                            const timeA = parseTimestamp(a.timestamp);
                                            const timeB = parseTimestamp(b.timestamp);
                                            return timeA - timeB;
                                        });
                                    })()}
                                    index="timestamp"
                                    categories={(() => {
                                        try {
                                            const firstPoint = timeSeriesResults[0];
                                            if (firstPoint?.emotions && typeof firstPoint.emotions === 'object') {
                                                // Use the complete emotion set to ensure all emotions appear in tooltips
                                                const allEmotions = ['happiness', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];
                                                return allEmotions.map(emotion => translateEmotionName(emotion, t));
                                            } else if (firstPoint?.emotion_scores && typeof firstPoint.emotion_scores === 'object') {
                                                // Use the complete emotion set to ensure all emotions appear in tooltips
                                                const allEmotions = ['happiness', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];
                                                return allEmotions.map(emotion => translateEmotionName(emotion, t));
                                            } else {
                                                return ["Confidence"];
                                            }
                                        } catch (e) {
                                            return ["Confidence"];
                                        }
                                    })()}
                                    colors={["emerald", "blue", "red", "violet", "orange", "lime", "gray"]}
                                    valueFormatter={(value) => `${value || 0}%`}
                                    yAxisWidth={48}
                                    showLegend={false}
                                    showGridLines={true}
                                    connectNulls={true}
                                    curveType="monotone"
                                    customTooltip={({ active, payload, label }) => {
                                        // Only show tooltip when hovering over actual data points with values
                                        if (!active || !payload || !payload.length || !label) {
                                            return null;
                                        }
                                        
                                        // Check if we have valid emotion data (not just hovering over axis)
                                        // Allow 0 values since they represent valid emotion data points
                                        const hasValidData = payload.some(item => 
                                            item.value !== undefined && 
                                            item.value !== null && 
                                            typeof item.value === 'number'
                                        );
                                        if (!hasValidData) {
                                            return null;
                                        }
                                        
                                        return (
                                            <div className="rounded-md border border-gray-200 bg-white shadow-md p-3 text-sm">
                                                <p className="font-medium text-gray-900 mb-2">{label}</p>
                                                <div className="space-y-1">
                                                    {payload.map((item, index) => {
                                                        // Show all emotions, including those with 0% values
                                                        const value = item.value !== undefined && item.value !== null ? item.value : 0;
                                                        return (
                                                            <div key={index} className="flex items-center justify-between space-x-4">
                                                                <div className="flex items-center space-x-2">
                                                                    <span 
                                                                        className="h-2 w-2 rounded-full"
                                                                        style={{ backgroundColor: item.color }}
                                                                    />
                                                                    <span className="text-gray-700">{item.category}</span>
                                                                </div>
                                                                <span className="font-medium text-gray-900">
                                                                    {value}%
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />

                                {/* Custom legend below chart */}
                                <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                    {[
                                        { name: translateEmotionName('happiness', t), color: '#10b981' },
                                        { name: translateEmotionName('sadness', t), color: '#3b82f6' },
                                        { name: translateEmotionName('anger', t), color: '#ef4444' },
                                        { name: translateEmotionName('fear', t), color: '#8b5cf6' },
                                        { name: translateEmotionName('surprise', t), color: '#f97316' },
                                        { name: translateEmotionName('disgust', t), color: '#65a30d' },
                                        { name: translateEmotionName('neutral', t), color: '#6b7280' }
                                    ].map((emotion) => (
                                        <div key={emotion.name} className="flex items-center space-x-2">
                                            <div 
                                                className="w-3 h-3 rounded" 
                                                style={{ backgroundColor: emotion.color }}
                                            ></div>
                                            <Text className="text-sm text-gray-700">{emotion.name}</Text>
                                        </div>
                                    ))}
                                </div>

                                <Text className="mt-2 text-xs text-gray-500">
                                    {timeSeriesResults[0]?.emotions || timeSeriesResults[0]?.emotion_scores 
                                        ? t('EmotionAnalysis.TimelineNote', `Showing emotion levels over time (${timeSeriesResults.length} data points)`)
                                        : t('EmotionAnalysis.TimelineConfidenceNote', `Showing confidence levels over time (${timeSeriesResults.length} data points)`)
                                    }
                                </Text>
                            </div>
                        )}
                        
                        {fragment.cost && fragment.cost > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                                <Text className="text-xs text-gray-500">
                                    {t('EmotionAnalysis.Cost', 'Analysis Cost')}: ${fragment.cost}
                                </Text>
                            </div>
                        )}
                    </div>
                </Card>
            );
        } catch (error) {
            console.error('Error rendering emotion analysis results:', error);
            return (
                <Card className="max-h-full" data-testid="emotion-analysis-card" {...rest}>
                    <div className="p-4">
                        <Text className="font-medium text-gray-900">
                            {t('EmotionAnalysis.Title', 'Audio Emotion Analysis')}
                        </Text>
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <Text className="text-yellow-700">
                                {t('EmotionAnalysis.DataError', 'Error displaying analysis results')}
                            </Text>
                        </div>
                    </div>
                </Card>
            );
        }
    }

    // Default fallback - don't show card when no data
    return null;
}

// Helper functions
function getStatusMessage(status, t) {
    switch (status) {
        case 'queued':
            return t('EmotionAnalysis.Status.Queued', 'Analysis queued for processing...');
        case 'processing':
            return t('EmotionAnalysis.Status.Processing', 'Processing audio emotions...');
        case 'pending_webhook':
            return t('EmotionAnalysis.Status.PendingResults', 'Analyzing audio emotions...');
        default:
            return t('EmotionAnalysis.Status.Unknown', 'Processing...');
    }
}

function translateEmotionName(emotion, t) {
    if (!emotion) return t('AudioEmotionSentiment.Emotions.Unknown', 'Unknown');
    
    const emotionKey = emotion.toLowerCase();
    switch (emotionKey) {
        case 'happiness':
            return t('AudioEmotionSentiment.Emotions.Happiness', 'Happiness');
        case 'sadness':
            return t('AudioEmotionSentiment.Emotions.Sadness', 'Sadness');
        case 'anger':
            return t('AudioEmotionSentiment.Emotions.Anger', 'Anger');
        case 'fear':
            return t('AudioEmotionSentiment.Emotions.Fear', 'Fear');
        case 'surprise':
            return t('AudioEmotionSentiment.Emotions.Surprise', 'Surprise');
        case 'disgust':
            return t('AudioEmotionSentiment.Emotions.Disgust', 'Disgust');
        case 'neutral':
            return t('AudioEmotionSentiment.Emotions.Neutral', 'Neutral');
        default:
            return emotion.charAt(0).toUpperCase() + emotion.slice(1);
    }
}

function getEmotionColor(emotion) {
    const colorMap = {
        'happiness': '#10b981', // emerald-500 - positive, growth, joy
        'sadness': '#3b82f6',   // blue-500 - melancholy, calm sadness
        'anger': '#ef4444',     // red-500 - intensity, aggression
        'fear': '#8b5cf6',      // violet-500 - uncertainty, anxiety
        'surprise': '#f97316',  // orange-500 - attention-grabbing, sudden
        'disgust': '#65a30d',   // lime-600 - unpleasant, earthy
        'neutral': '#6b7280',   // gray-500 - balanced, no emotion
    };
    return colorMap[emotion?.toLowerCase()] || '#6b7280';
}


function formatDuration(seconds) {
    if (!seconds) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
}

function formatTimestamp(seconds) {
    if (!seconds) return '0:00';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Helper functions for processing segments data
function calculateEmotionDistributionFromSegments(segments) {
    if (!Array.isArray(segments) || segments.length === 0) {
        return {};
    }
    
    const emotionTotals = {};
    let totalSegments = 0;
    
    segments.forEach(segment => {
        if (segment.emotions && typeof segment.emotions === 'object') {
            Object.entries(segment.emotions).forEach(([emotion, score]) => {
                emotionTotals[emotion] = (emotionTotals[emotion] || 0) + (typeof score === 'number' ? score : 0);
            });
            totalSegments++;
        } else if (segment.dominant_emotion) {
            const emotion = segment.dominant_emotion.toLowerCase();
            const confidence = segment.confidence || 1;
            emotionTotals[emotion] = (emotionTotals[emotion] || 0) + confidence;
            totalSegments++;
        }
    });
    
    // Calculate averages
    const distribution = {};
    Object.entries(emotionTotals).forEach(([emotion, total]) => {
        distribution[emotion] = totalSegments > 0 ? total / totalSegments : 0;
    });
    
    return distribution;
}

function getDominantEmotionFromDistribution(emotionDistribution) {
    if (!emotionDistribution || Object.keys(emotionDistribution).length === 0) {
        return 'neutral';
    }
    
    let maxEmotion = 'neutral';
    let maxValue = 0;
    
    Object.entries(emotionDistribution).forEach(([emotion, value]) => {
        if (value > maxValue) {
            maxValue = value;
            maxEmotion = emotion;
        }
    });
    
    return maxEmotion;
}

function calculateAverageConfidence(segments) {
    if (!Array.isArray(segments) || segments.length === 0) {
        return 0;
    }
    
    const confidenceValues = segments
        .map(segment => segment.confidence || 0)
        .filter(confidence => confidence > 0);
    
    if (confidenceValues.length === 0) {
        return 0;
    }
    
    const sum = confidenceValues.reduce((total, confidence) => total + confidence, 0);
    return sum / confidenceValues.length;
}

export default AudioEmotionAnalysisCard;